import { useCallback, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  bannerReopened,
  consentChosen,
  selectCanManagePreferences,
  selectConsent,
  selectShowBanner
} from "../store/slices/consentSlice";
import type { CookieConsentState } from "../services/cookieConsent/types";

export interface CookieConsentContextValue {
  consent: CookieConsentState;
  showBanner: boolean;
  canManagePreferences: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  updateConsent: (analytics: boolean, functional: boolean) => void;
  reopenBanner: () => void;
}

export const useCookieConsent = (): CookieConsentContextValue => {
  const dispatch = useAppDispatch();
  const consent = useAppSelector(selectConsent);
  const showBanner = useAppSelector(selectShowBanner);
  const canManagePreferences = useAppSelector(selectCanManagePreferences);
  const acceptAll = useCallback(
    () => dispatch(consentChosen({ analytics: true, functional: true })),
    [dispatch]
  );
  const rejectNonEssential = useCallback(
    () => dispatch(consentChosen({ analytics: false, functional: false })),
    [dispatch]
  );
  const updateConsent = useCallback(
    (analytics: boolean, functional: boolean) =>
      dispatch(consentChosen({ analytics, functional })),
    [dispatch]
  );
  const reopenBanner = useCallback(
    () => dispatch(bannerReopened()),
    [dispatch]
  );

  return useMemo(
    () => ({
      consent,
      showBanner,
      canManagePreferences,
      acceptAll,
      rejectNonEssential,
      updateConsent,
      reopenBanner
    }),
    [
      consent,
      showBanner,
      canManagePreferences,
      acceptAll,
      rejectNonEssential,
      updateConsent,
      reopenBanner
    ]
  );
};
